import { useEffect, useRef, useState } from "react";
import SectionHeader from "@/components/SectionHeader";
import Reveal from "@/components/Reveal";

// Dashboard screenshots: localized + optimized. Were 1920px PNGs served from
// Lovable's CDN (~890 KB total) → AVIF/WebP in /public/dashboard (~190 KB, ~78%
// smaller) and same-origin so they also work on the Vercel build.

type Slide = {
  id: string;
  role: string;
  eyebrow: string;
  title: string;
  blurb: string;
  link: string;
  image: { avif: string; webp: string };
  alt: string;
  accent: string;
};

const SLIDES: Slide[] = [
  {
    id: "growth",
    role: "Roles",
    eyebrow: "Any Role",
    title: "A specialist for every task",
    blurb:
      "Spin up any role with its own memory, prompts and starter plays. From growth to sales to operations, every agent is trained for the job.",
    link: "Explore agent roles",
    image: { avif: "/dashboard/growth.avif", webp: "/dashboard/growth.webp" },
    alt: "Mane dashboard: Agent roles",
    accent: "#34d399",
  },
  {
    id: "sales",
    role: "Workflows",
    eyebrow: "Workflows",
    title: "Outbound that compounds across sessions",
    blurb:
      "Prospecting, personalized sequences and meeting booking under one role. The agent keeps history across runs so it stops repeating itself.",
    link: "See sales workflows",
    image: { avif: "/dashboard/sales-lead.avif", webp: "/dashboard/sales-lead.webp" },
    alt: "Mane dashboard: Sales Development Lead agent",
    accent: "#fbbf24",
  },
  {
    id: "tasks",
    role: "Tasks",
    eyebrow: "Operations",
    title: "Agents work the board with you",
    blurb:
      "A real Kanban your agents can read and write. They claim tickets, post updates, and hand off cleanly, so you manage them like a team.",
    link: "See task management",
    image: { avif: "/dashboard/tasks.avif", webp: "/dashboard/tasks.webp" },
    alt: "Mane dashboard: Tasks Kanban board",
    accent: "#60a5fa",
  },
  {
    id: "scheduled",
    role: "Schedule",
    eyebrow: "Recurring runs",
    title: "Schedule the work, not the prompts",
    blurb:
      "Weekly reports, bi weekly research, daily digests. Cron level scheduling for full agent workflows, with calendar and list views.",
    link: "See scheduled runs",
    image: { avif: "/dashboard/scheduled.avif", webp: "/dashboard/scheduled.webp" },
    alt: "Mane dashboard: Scheduled automations",
    accent: "#c084fc",
  },
  {
    id: "artifacts",
    role: "Ship",
    eyebrow: "Deliverables",
    title: "Every artifact, versioned and live",
    blurb:
      "Landing pages, pricing experiments, careers boards. Agents ship to staging or live, and you keep the audit trail.",
    link: "Browse artifacts",
    image: { avif: "/dashboard/artifacts.avif", webp: "/dashboard/artifacts.webp" },
    alt: "Mane dashboard: Websites artifacts gallery",
    accent: "#fb7185",
  },
  {
    id: "wikis",
    role: "Memory",
    eyebrow: "Memory",
    title: "The brain agents read from. And write back to",
    blurb:
      "Company, services, SEO, brand, policies. One source of truth that every agent uses, so nothing has to be re explained.",
    link: "See the wiki",
    image: { avif: "/dashboard/wikis.avif", webp: "/dashboard/wikis.webp" },
    alt: "Mane dashboard: Wiki workspace",
    accent: "#5eead4",
  },
];

const AUTOPLAY_MS = 7000;

const usePrefersReducedMotion = () => {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);

  return reduced;
};

/**
 * Bezel that frames the screenshot. The screenshots themselves are dark UI,
 * so we keep a dark bezel in both themes — it reads as a "device" rather
 * than a panel and prevents jarring contrast against the screenshot edges.
 */
const WindowFrame = ({ activeId, slides }: { activeId: string; slides: Slide[] }) => {
  return (
    <div
      className="relative w-full overflow-hidden rounded-[14px] bg-[#0b0b0d] dark:bg-[#0b0b0d]"
      style={{
        boxShadow:
          "0 0 0 1px rgba(255,255,255,0.08), 0 1px 0 rgba(255,255,255,0.06) inset, 0 40px 80px -30px rgba(0,0,0,0.55), 0 16px 40px -16px rgba(0,0,0,0.4)",
        aspectRatio: "16 / 10",
      }}
    >
      {slides.map((s, i) => {
        const isActive = s.id === activeId;
        return (
          <picture key={s.id}>
            <source srcSet={s.image.avif} type="image/avif" />
            <source srcSet={s.image.webp} type="image/webp" />
            <img
              src={s.image.webp}
              alt={s.alt}
              loading="eager"
              decoding="async"
              // @ts-expect-error fetchpriority not yet in React types
              fetchpriority={i === 0 ? "high" : "low"}
              draggable={false}
              className="absolute inset-0 block w-full h-full object-contain object-center transition-opacity duration-300 ease-out"
              style={{ opacity: isActive ? 1 : 0, willChange: "opacity" }}
              aria-hidden={!isActive}
            />
          </picture>
        );
      })}
    </div>
  );
};

const DashboardShowcase = () => {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const reduced = usePrefersReducedMotion();
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (paused || reduced) return;
    const t = setTimeout(() => setActive((i) => (i + 1) % SLIDES.length), AUTOPLAY_MS);
    return () => clearTimeout(t);
  }, [active, paused, reduced]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => setPaused(!entry.isIntersecting),
      { threshold: 0.2 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const slide = SLIDES[active];

  return (
    <section id="dashboard-showcase" className="py-20 sm:py-28">
      <style>{`
        @keyframes dashboardProgress {
          from { transform: scaleX(0); }
          to { transform: scaleX(1); }
        }
      `}</style>
      <div className="max-w-content mx-auto px-5 sm:px-8">
        <Reveal>
          <SectionHeader
            align="center"
            eyebrow="Inside the dashboard"
            title={
              <>
                Your team's <em>command center</em>
              </>
            }
            subtitle="Roles, work-flows, schedules, artifacts and a shared wikis and memory."
          />
        </Reveal>

        <Reveal>
          <div
            ref={containerRef}
            className="relative mx-auto mt-10"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
          >
            {/* Editorial card — adapts cleanly to light & dark */}
            <div
              className="
                relative rounded-[24px] overflow-hidden
                bg-gradient-to-b from-[#f6f3ec] to-[#efece4]
                dark:from-[#111114] dark:to-[#08080a]
                shadow-[0_0_0_1px_rgba(0,0,0,0.06),0_40px_90px_-40px_rgba(20,16,8,0.25)]
                dark:shadow-[0_0_0_1px_rgba(255,255,255,0.06),0_60px_140px_-40px_rgba(0,0,0,0.7)]
              "
            >
              {/* Grid texture — black ink in light, white ink in dark */}
              <div
                aria-hidden
                className="absolute inset-0 pointer-events-none opacity-[0.5] dark:opacity-[0.35]"
                style={{
                  backgroundImage:
                    "linear-gradient(currentColor 1px, transparent 1px), linear-gradient(90deg, currentColor 1px, transparent 1px)",
                  backgroundSize: "48px 48px",
                  color: "rgba(0,0,0,0.04)",
                  maskImage:
                    "radial-gradient(120% 80% at 50% 0%, #000 30%, transparent 80%)",
                  WebkitMaskImage:
                    "radial-gradient(120% 80% at 50% 0%, #000 30%, transparent 80%)",
                }}
              />
              <div
                aria-hidden
                className="absolute inset-0 pointer-events-none opacity-0 dark:opacity-100"
                style={{
                  backgroundImage:
                    "linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)",
                  backgroundSize: "48px 48px",
                  maskImage:
                    "radial-gradient(120% 80% at 50% 0%, #000 30%, transparent 80%)",
                  WebkitMaskImage:
                    "radial-gradient(120% 80% at 50% 0%, #000 30%, transparent 80%)",
                }}
              />

              {/* Soft ambient color halo, accent-tinted, works on both themes */}
              <div
                aria-hidden
                key={slide.id + "-halo"}
                className="absolute inset-0 pointer-events-none"
                style={{
                  background: `radial-gradient(60% 50% at 78% 18%, ${slide.accent}33 0%, transparent 70%), radial-gradient(50% 40% at 12% 90%, ${slide.accent}1f 0%, transparent 70%)`,
                  opacity: 1,
                }}
              />

              <div className="relative grid gap-10 lg:gap-12 lg:grid-cols-[minmax(260px,340px)_1fr] items-center p-6 sm:p-10 lg:p-14">
                <div className="relative z-10">
                    <div key={slide.id + "-copy"}>
                      <div className="flex items-center gap-2 mb-4">
                        <span
                          className="h-1.5 w-1.5 rounded-full"
                          style={{ background: slide.accent, boxShadow: `0 0 12px ${slide.accent}` }}
                        />
                        <div className="text-[10px] uppercase tracking-[0.22em] font-mono text-foreground/55">
                          {slide.eyebrow}
                        </div>
                      </div>
                      <h3 className="h-block text-foreground">
                        {slide.title}
                      </h3>
                      <p className="mt-4 text-[15px] leading-[1.6] text-foreground/70">
                        {slide.blurb}
                      </p>
                    </div>
                </div>

                <div className="relative">
                  <WindowFrame activeId={slide.id} slides={SLIDES} />
                </div>
              </div>
            </div>

            {/* Tab strip — minimal, integrated */}
            <div className="mt-5 flex justify-center">
              <div className="inline-flex items-center gap-0.5 rounded-full border border-black/[0.06] dark:border-white/[0.08] bg-black/[0.02] dark:bg-white/[0.03] p-1">
                {SLIDES.map((s, i) => {
                  const isActive = i === active;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => setActive(i)}
                      className={`relative rounded-full px-3 py-1.5 text-[11px] font-medium tracking-[-0.01em] transition-all
                        ${isActive
                          ? "text-foreground bg-white dark:bg-[#1a1a1c] shadow-[0_0_0_1px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.04)] dark:shadow-[0_0_0_1px_rgba(255,255,255,0.08),0_1px_2px_rgba(0,0,0,0.2)]"
                          : "text-foreground/40 hover:text-foreground/70 dark:text-white/35 dark:hover:text-white/65"
                        }`}
                      aria-pressed={isActive}
                    >
                      {s.role}
                      {isActive && !reduced && !paused && (
                        <span
                          key={`pb-${active}`}
                          className="absolute left-1 right-1 bottom-[2px] h-[2px] rounded-full"
                          style={{
                            background: s.accent,
                            transformOrigin: "left center",
                            animation: `dashboardProgress ${AUTOPLAY_MS}ms linear both`,
                          }}
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default DashboardShowcase;
